/**
 * Per-page search and social metadata for the public site.
 *
 * Single source of truth: the <Seo> component applies these in the browser and
 * the build-time prerender (scripts/seo-build.mjs) writes the same values into
 * static HTML, so a link pasted into Slack, WhatsApp or LinkedIn gets a real
 * title, description and image without the preview bot running JavaScript.
 */

import {
  awards,
  business,
  cateringPackages,
  cafePartners,
  coreMenu,
  seasonalMenu,
  serviceArea,
  social,
  wholesaleFaq,
} from '../content/siteContent.mjs';

export const SITE_URL = (process.env.REACT_APP_SITE_URL || 'https://maddonutshop.com').replace(/\/+$/, '');

export const OG_IMAGE = '/img/og-share.png';

const abs = (path) => `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;

const breadcrumbs = (trail) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [{ name: 'Home', path: '/' }, ...trail].map((item, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: item.name,
    item: abs(item.path),
  })),
});

const bakeryJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'Bakery',
  '@id': `${SITE_URL}/#bakery`,
  name: business.name,
  description: business.description,
  url: SITE_URL,
  email: business.email,
  image: abs('/img/hero-apple-fritter.webp'),
  logo: abs('/img/logo.webp'),
  slogan: business.tagline,
  priceRange: '$$',
  servesCuisine: 'Donuts',
  address: {
    '@type': 'PostalAddress',
    addressLocality: business.locality,
    addressRegion: 'NY',
    addressCountry: 'US',
  },
  areaServed: serviceArea.map((town) => ({ '@type': 'City', name: town })),
  sameAs: social.map((s) => s.url),
  award: awards.map((a) => `${a.source} ${a.title} ${a.year}`),
  makesOffer: {
    '@type': 'Offer',
    itemOffered: {
      '@type': 'Service',
      name: 'Wholesale donut supply',
      serviceType: 'Wholesale bakery supply',
      areaServed: `${business.region} and ${business.nearby}`,
    },
  },
});

const websiteJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${SITE_URL}/#website`,
  name: business.name,
  url: SITE_URL,
  publisher: { '@id': `${SITE_URL}/#bakery` },
});

const menuJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'Menu',
  name: 'MAD Donuts menu',
  url: abs('/menu'),
  hasMenuSection: [
    { name: 'Signature donuts', items: coreMenu },
    { name: 'Seasonal specials', items: seasonalMenu },
  ].map((section) => ({
    '@type': 'MenuSection',
    name: section.name,
    hasMenuItem: section.items.map((item) => ({
      '@type': 'MenuItem',
      name: item.name,
      description: item.description,
      image: abs(item.image.src),
    })),
  })),
});

const faqJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: wholesaleFaq.map((entry) => ({
    '@type': 'Question',
    name: entry.question,
    acceptedAnswer: { '@type': 'Answer', text: entry.answer },
  })),
});

const wholesaleServiceJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Wholesale donut supply',
  serviceType: 'Wholesale donut supplier',
  provider: { '@id': `${SITE_URL}/#bakery` },
  areaServed: serviceArea.map((town) => ({ '@type': 'City', name: town })),
  url: abs('/wholesale'),
  description: `Fresh donuts baked overnight in ${business.baseTown} and delivered to cafés, restaurants and offices across ${business.region} and ${business.nearby}. Orders close ${business.leadTime} before delivery.`,
});

const cateringServiceJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Donut catering',
  serviceType: 'Event and office catering',
  provider: { '@id': `${SITE_URL}/#bakery` },
  areaServed: { '@type': 'AdministrativeArea', name: business.region },
  url: abs('/catering'),
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Donut catering packages',
    itemListElement: cateringPackages.map((pkg) => ({
      '@type': 'Offer',
      name: pkg.name,
      description: `${pkg.description} ${pkg.includes}. Serves ${pkg.servings}.`,
      priceSpecification: {
        '@type': 'PriceSpecification',
        price: pkg.priceValue,
        priceCurrency: 'USD',
        valueAddedTaxIncluded: false,
        description: `${pkg.price} — final price confirmed on quote`,
      },
    })),
  },
});

const partnersJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Cafés stocking MAD Donuts',
  itemListElement: cafePartners.map((partner, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    item: {
      '@type': 'CafeOrCoffeeShop',
      name: partner.name,
      url: partner.website,
      address: { '@type': 'PostalAddress', addressLocality: partner.location },
    },
  })),
});

export const PAGES = [
  {
    path: '/',
    title: 'Wholesale Donuts in Westchester County | MAD Donuts',
    description:
      'Award-winning donuts baked overnight in Pleasantville, Westchester and delivered fresh to cafés, restaurants and offices across Westchester County and nearby Connecticut.',
    heading: 'Wholesale donuts for Westchester counters',
    jsonLd: () => [bakeryJsonLd(), websiteJsonLd()],
    sitemap: { priority: '1.0', changefreq: 'monthly' },
  },
  {
    path: '/wholesale',
    title: 'Wholesale Donut Supplier — Westchester & Connecticut',
    description:
      'Become a MAD Donuts wholesale partner: morning delivery across Westchester and into Connecticut, orders two business days ahead, vegan options and returns credit.',
    heading: 'Wholesale donut supply across Westchester and Connecticut',
    jsonLd: () => [wholesaleServiceJsonLd(), faqJsonLd(), breadcrumbs([{ name: 'Wholesale', path: '/wholesale' }])],
    sitemap: { priority: '0.9', changefreq: 'monthly' },
  },
  {
    path: '/menu',
    title: 'Donut Menu — Flavours, Seasonal Specials & Vegan',
    description:
      'The full MAD Donuts range: brioche and buttermilk classics, house-made jam fillings, weekly seasonal specials and vegan versions of most flavours.',
    heading: 'Our donut menu',
    jsonLd: () => [menuJsonLd(), breadcrumbs([{ name: 'Menu', path: '/menu' }])],
    sitemap: { priority: '0.8', changefreq: 'weekly' },
  },
  {
    path: '/catering',
    title: 'Donut Catering in Westchester — Offices & Events',
    description:
      'Donut catering for offices, meetings and events in Westchester: packages from 24 to 100+ donuts with delivery included. Request a quote in under a minute.',
    heading: 'Donut catering for offices and events',
    jsonLd: () => [cateringServiceJsonLd(), breadcrumbs([{ name: 'Catering', path: '/catering' }])],
    sitemap: { priority: '0.8', changefreq: 'monthly' },
  },
  {
    path: '/where-to-find-us',
    title: 'Where to Buy MAD Donuts — Café Partners',
    description:
      'Find MAD Donuts on the counter at independent cafés in Pleasantville, Dobbs Ferry, Irvington, Tarrytown, Bronxville, Pelham, Larchmont, White Plains, Peekskill, Mt Kisco and Greenwich.',
    heading: 'Where to find MAD Donuts today',
    jsonLd: () => [partnersJsonLd(), breadcrumbs([{ name: 'Where to find us', path: '/where-to-find-us' }])],
    sitemap: { priority: '0.7', changefreq: 'monthly' },
  },
  {
    path: '/about',
    title: 'About MAD Donuts — Best of Westchester Winner',
    description:
      'How a Westchester bakery became a two-time Best of Westchester winner for Best Donuts, and how the wholesale kitchen bakes and delivers today.',
    heading: 'About MAD Donuts',
    jsonLd: () => [breadcrumbs([{ name: 'About & awards', path: '/about' }])],
    sitemap: { priority: '0.6', changefreq: 'yearly' },
  },
  {
    path: '/contact',
    title: 'Contact MAD Donuts — Wholesale & Catering',
    description:
      'Send a wholesale or catering inquiry, or email info@maddonutshop.com. Delivery across Westchester County and nearby Connecticut from Pleasantville, NY.',
    heading: 'Contact MAD Donuts',
    jsonLd: () => [bakeryJsonLd(), breadcrumbs([{ name: 'Contact', path: '/contact' }])],
    sitemap: { priority: '0.6', changefreq: 'yearly' },
  },
];

export const NOT_FOUND_SEO = {
  path: '/404',
  title: 'Page not found | MAD Donuts',
  description: 'That page has moved or never existed. Browse the donut menu, wholesale supply or catering instead.',
  noindex: true,
};

export const getPageSeo = (pathname) => {
  const clean = pathname.replace(/\/+$/, '') || '/';
  return PAGES.find((p) => p.path === clean) || null;
};

/** Portal and utility routes must never be indexed. */
export const NOINDEX_PREFIXES = ['/client', '/admin', '/staff', '/reset-password'];

export const isNoindexPath = (pathname) =>
  NOINDEX_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));

export const absoluteUrl = abs;
